import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../utils/supabase';
import { Bot, Save, MessageSquare, Globe, Smile, Clock, ShoppingBag, MapPin, CheckCircle, Zap } from 'lucide-react';

export default function BotSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [restaurantId, setRestaurantId] = useState(null);
  const [previewMessage, setPreviewMessage] = useState('');

  const [form, setForm] = useState({
    bot_name: 'TabBot',
    bot_language: 'both',
    bot_tone: 'friendly',
    bot_welcome: '',
    bot_closed: '',
    bot_upsell: true,
    bot_min_order: 0,
    bot_delivery_areas: '',
    bot_active: false,
  });

  const generatePreview = useCallback(() => {
    const greetings = {
      friendly: { ar: 'أهلاً وسهلاً! 😊', en: 'Hey there! 😊', both: 'أهلاً! Hey! 😊' },
      formal: { ar: 'مرحباً بكم.', en: 'Welcome.', both: 'مرحباً. Welcome.' },
      fun: { ar: 'يسعد صباحك! 🎉', en: 'Woohoo! 🎉', both: 'يسعد صباحك! Woohoo! 🎉' },
    };
    const greeting = greetings[form.bot_tone]?.[form.bot_language] || 'أهلاً!';
    const custom = form.bot_welcome?.trim();
    setPreviewMessage(custom || `${greeting}\nأنا ${form.bot_name}. كيف أقدر أساعدك؟`);
  }, [form]);

  useEffect(() => {
    fetchBotSettings();
  }, []);

  useEffect(() => {
    generatePreview();
    // eslint-disable-next-line
  }, [form.bot_tone, form.bot_language, form.bot_name, form.bot_welcome]);

  const fetchBotSettings = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: rest } = await supabase
        .from('restaurants')
        .select('*')
        .eq('email', user.email)
        .single();

      if (!rest) return;
      setRestaurantId(rest.id);

      setForm({
        bot_name: rest.bot_name || 'TabBot',
        bot_language: rest.bot_language || 'both',
        bot_tone: rest.bot_tone || 'friendly',
        bot_welcome: rest.bot_welcome || '',
        bot_closed: rest.bot_closed || '',
        bot_upsell: rest.bot_upsell ?? true,
        bot_min_order: rest.bot_min_order || 0,
        bot_delivery_areas: rest.bot_delivery_areas || '',
        bot_active: rest.bot_active ?? false,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!restaurantId) return;
    setSaving(true);
    setSuccess(false);
    try {
      const { error } = await supabase
        .from('restaurants')
        .update({
          bot_name: form.bot_name.trim(),
          bot_language: form.bot_language,
          bot_tone: form.bot_tone,
          bot_welcome: form.bot_welcome.trim(),
          bot_closed: form.bot_closed.trim(),
          bot_upsell: form.bot_upsell,
          bot_min_order: Number(form.bot_min_order),
          bot_delivery_areas: form.bot_delivery_areas.trim(),
          bot_active: form.bot_active,
        })
        .eq('id', restaurantId);

      if (error) throw error;
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ value, onChange, size = 'md' }) => (
    <button
      onClick={() => onChange(!value)}
      className={`relative inline-flex rounded-full transition-colors shrink-0 ${
        value ? 'bg-primary' : 'bg-gray-200'
      } ${size === 'lg' ? 'w-14 h-7' : 'w-11 h-6'}`}
    >
      <span className={`absolute top-1 bg-white rounded-full shadow transition-transform ${
        size === 'lg'
          ? `w-5 h-5 ${value ? 'translate-x-8' : 'translate-x-1'}`
          : `w-4 h-4 ${value ? 'translate-x-6' : 'translate-x-1'}`
      }`} />
    </button>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse h-48" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Bot Settings</h2>
          <p className="text-sm text-gray-400 mt-0.5">Customize how TabBot talks to your customers</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-secondary transition disabled:opacity-60 shadow-sm"
        >
          {saving ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Saving...
            </>
          ) : (
            <>
              <Save size={16} />
              Save Changes
            </>
          )}
        </button>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2">
          <CheckCircle size={16} className="text-green-500" />
          Bot settings saved successfully!
        </div>
      )}

      {/* Bot Status */}
      <div className={`rounded-2xl border shadow-sm p-5 transition ${form.bot_active ? 'bg-green-50 border-green-200' : 'bg-white border-gray-100'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${form.bot_active ? 'bg-green-100' : 'bg-gray-100'}`}>
              <Bot size={28} className={form.bot_active ? 'text-green-600' : 'text-gray-400'} />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-lg">WhatsApp Bot</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <div className={`w-2 h-2 rounded-full ${form.bot_active ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                <p className={`text-sm font-medium ${form.bot_active ? 'text-green-600' : 'text-gray-400'}`}>
                  {form.bot_active ? 'Active — Replying to customers 24/7' : 'Inactive — Bot is turned off'}
                </p>
              </div>
            </div>
          </div>
          <Toggle value={form.bot_active} onChange={v => setForm({ ...form, bot_active: v })} size="lg" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Left Column */}
        <div className="space-y-6">

          {/* Bot Identity */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <Bot size={18} className="text-gray-400" />
              <h3 className="font-bold text-gray-800">Bot Identity</h3>
            </div>
            <div className="p-6">
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Bot Name</label>
              <input
                value={form.bot_name}
                onChange={e => setForm({ ...form, bot_name: e.target.value })}
                placeholder="TabBot"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
              <p className="text-xs text-gray-400 mt-2">This name appears when the bot introduces itself to customers</p>
            </div>
          </div>

          {/* Language */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <Globe size={18} className="text-gray-400" />
              <h3 className="font-bold text-gray-800">Reply Language</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'arabic', label: 'Arabic', flag: '🇸🇦', desc: 'عربي فقط' },
                  { value: 'english', label: 'English', flag: '🇬🇧', desc: 'English only' },
                  { value: 'both', label: 'Both', flag: '🌍', desc: 'عربي & English' },
                ].map(lang => (
                  <button
                    key={lang.value}
                    onClick={() => setForm({ ...form, bot_language: lang.value })}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition
                      ${form.bot_language === lang.value ? 'border-primary bg-primary/5' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <span className="text-xs font-bold text-gray-700">{lang.label}</span>
                    <span className="text-xs text-gray-400">{lang.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tone */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <Smile size={18} className="text-gray-400" />
              <h3 className="font-bold text-gray-800">Bot Tone</h3>
            </div>
            <div className="p-6 space-y-2">
              {[
                { value: 'friendly', label: 'Friendly', desc: 'Warm and welcoming with emojis', emoji: '😊' },
                { value: 'formal', label: 'Formal', desc: 'Professional and respectful', emoji: '👔' },
                { value: 'fun', label: 'Fun & Energetic', desc: 'Exciting and enthusiastic', emoji: '🎉' },
              ].map(tone => (
                <button
                  key={tone.value}
                  onClick={() => setForm({ ...form, bot_tone: tone.value })}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition text-left
                    ${form.bot_tone === tone.value ? 'border-primary bg-primary/5' : 'border-gray-100 hover:border-gray-200'}`}
                >
                  <span className="text-2xl">{tone.emoji}</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{tone.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{tone.desc}</p>
                  </div>
                  {form.bot_tone === tone.value && (
                    <CheckCircle size={18} className="text-primary shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Right Column */}
        <div className="space-y-6">

          {/* Live Preview */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare size={18} className="text-gray-400" />
                <h3 className="font-bold text-gray-800">Live Preview</h3>
              </div>
              <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-lg font-medium flex items-center gap-1">
                <Zap size={10} /> Live
              </span>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4">
                {/* Customer message */}
                <div className="flex justify-end mb-3">
                  <div className="bg-primary text-white text-sm px-4 py-2.5 rounded-2xl rounded-tr-sm max-w-xs">
                    Hi! 👋
                  </div>
                </div>
                {/* Bot reply */}
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center shrink-0">
                    <Bot size={16} className="text-white" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm max-w-xs">
                    <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{previewMessage}</p>
                    <p className="text-xs text-gray-300 mt-2 text-right">
                      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Custom Messages */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <MessageSquare size={18} className="text-gray-400" />
              <h3 className="font-bold text-gray-800">Custom Messages</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Welcome Message</label>
                <textarea
                  value={form.bot_welcome}
                  onChange={e => setForm({ ...form, bot_welcome: e.target.value })}
                  placeholder="Leave empty to auto-generate based on tone..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none transition"
                  rows={3}
                />
                <p className="text-xs text-gray-400 mt-1">Sent when a customer messages for the first time</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-1">
                  <Clock size={13} className="text-gray-400" /> Closed Hours Message
                </label>
                <textarea
                  value={form.bot_closed}
                  onChange={e => setForm({ ...form, bot_closed: e.target.value })}
                  placeholder="e.g. Sorry, we're closed now. We open tomorrow at 11am! 🌙"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none transition"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Order Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <ShoppingBag size={18} className="text-gray-400" />
              <h3 className="font-bold text-gray-800">Order Settings</h3>
            </div>
            <div className="p-6 space-y-4">

              <div className="flex items-center justify-between py-1">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Auto Upsell</p>
                  <p className="text-xs text-gray-400 mt-0.5">Bot suggests add-ons to increase order value</p>
                </div>
                <Toggle value={form.bot_upsell} onChange={v => setForm({ ...form, bot_upsell: v })} />
              </div>

              <div className="border-t border-gray-50 pt-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Minimum Order (SAR)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">SAR</span>
                  <input
                    type="number"
                    value={form.bot_min_order}
                    onChange={e => setForm({ ...form, bot_min_order: e.target.value })}
                    placeholder="0"
                    className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Bot will inform customers if their order is below minimum</p>
              </div>

              <div className="border-t border-gray-50 pt-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-1">
                  <MapPin size={13} className="text-gray-400" /> Delivery Areas
                </label>
                <textarea
                  value={form.bot_delivery_areas}
                  onChange={e => setForm({ ...form, bot_delivery_areas: e.target.value })}
                  placeholder="e.g. Al Rawdah, Al Hamra, Al Zahraa, Al Andalus"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none transition"
                  rows={2}
                />
                <p className="text-xs text-gray-400 mt-1">Separate areas with commas</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Save Bottom */}
      <div className="flex justify-end pb-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-medium hover:bg-secondary transition disabled:opacity-60 shadow-sm"
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

    </div>
  );
}